import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, TranscodeProfile, CreateTranscodeProfileRequest } from '@/lib/api'
import { Plus, Settings2, Edit, Trash2, X, Lock, Copy, Code } from 'lucide-react'

interface ProfileFormData {
  name: string
  preset: 'passthrough' | 'universal' | 'low_bandwidth' | 'audio_only' | 'custom'
  description: string
  video_codec: string
  video_width: number
  video_height: number
  video_bitrate: number
  video_fps: number
  video_profile: string
  audio_codec: string
  audio_bitrate: number
  audio_sample_rate: number
  audio_channels: number
  custom_ffmpeg_args: string
}

const defaultFormData: ProfileFormData = {
  name: '',
  preset: 'passthrough',
  description: '',
  video_codec: '',
  video_width: 0,
  video_height: 0,
  video_bitrate: 0,
  video_fps: 0,
  video_profile: '',
  audio_codec: '',
  audio_bitrate: 0,
  audio_sample_rate: 0,
  audio_channels: 0,
  custom_ffmpeg_args: '',
}

const presetDescriptions: Record<string, string> = {
  passthrough: 'No transcoding - copy streams directly',
  universal: 'H.264 + AAC for maximum compatibility',
  low_bandwidth: '720p at ~2Mbps for slow connections',
  audio_only: 'Extract audio only (AAC 192kbps)',
  custom: 'Define your own settings',
}

export default function Transcoding() {
  const [modalOpen, setModalOpen] = useState(false)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [editingProfile, setEditingProfile] = useState<TranscodeProfile | null>(null)
  const [formData, setFormData] = useState<ProfileFormData>(defaultFormData)
  const [previewArgs, setPreviewArgs] = useState<string[]>([])
  const [error, setError] = useState('')
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['transcodeProfiles'],
    queryFn: () => api.getTranscodeProfiles(),
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateTranscodeProfileRequest) => api.createTranscodeProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcodeProfiles'] })
      closeModal()
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to create profile')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateTranscodeProfileRequest }) =>
      api.updateTranscodeProfile(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcodeProfiles'] })
      closeModal()
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to update profile')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteTranscodeProfile(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcodeProfiles'] })
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to delete profile')
    },
  })

  const previewMutation = useMutation({
    mutationFn: (data: CreateTranscodeProfileRequest) => api.previewTranscodeArgs(data),
    onSuccess: (data) => {
      setPreviewArgs(data.args || [])
      setPreviewModalOpen(true)
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to preview arguments')
    },
  })

  const openCreateModal = () => {
    setEditingProfile(null)
    setFormData(defaultFormData)
    setError('')
    setModalOpen(true)
  }

  const openEditModal = (profile: TranscodeProfile) => {
    if (profile.is_system) return
    setEditingProfile(profile)
    setFormData({
      name: profile.name,
      preset: profile.preset as any,
      description: profile.description || '',
      video_codec: profile.video_codec || '',
      video_width: profile.video_width || 0,
      video_height: profile.video_height || 0,
      video_bitrate: profile.video_bitrate || 0,
      video_fps: profile.video_fps || 0,
      video_profile: profile.video_profile || '',
      audio_codec: profile.audio_codec || '',
      audio_bitrate: profile.audio_bitrate || 0,
      audio_sample_rate: profile.audio_sample_rate || 0,
      audio_channels: profile.audio_channels || 0,
      custom_ffmpeg_args: profile.custom_ffmpeg_args || '',
    })
    setError('')
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingProfile(null)
    setFormData(defaultFormData)
    setError('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const submitData: CreateTranscodeProfileRequest = {
      name: formData.name,
      preset: formData.preset,
      description: formData.description || undefined,
      video_codec: formData.video_codec || undefined,
      video_width: formData.video_width || undefined,
      video_height: formData.video_height || undefined,
      video_bitrate: formData.video_bitrate || undefined,
      video_fps: formData.video_fps || undefined,
      video_profile: formData.video_profile || undefined,
      audio_codec: formData.audio_codec || undefined,
      audio_bitrate: formData.audio_bitrate || undefined,
      audio_sample_rate: formData.audio_sample_rate || undefined,
      audio_channels: formData.audio_channels || undefined,
      custom_ffmpeg_args: formData.custom_ffmpeg_args || undefined,
    }

    if (editingProfile) {
      updateMutation.mutate({ id: editingProfile.id, data: submitData })
    } else {
      createMutation.mutate(submitData)
    }
  }

  const handleDelete = (profile: TranscodeProfile) => {
    if (profile.is_system) return
    if (confirm(`Delete profile "${profile.name}"?`)) {
      deleteMutation.mutate(profile.id)
    }
  }

  const handlePreview = () => {
    previewMutation.mutate({
      name: formData.name || 'Preview',
      preset: formData.preset,
      video_codec: formData.video_codec || undefined,
      video_width: formData.video_width || undefined,
      video_height: formData.video_height || undefined,
      video_bitrate: formData.video_bitrate || undefined,
      video_fps: formData.video_fps || undefined,
      video_profile: formData.video_profile || undefined,
      audio_codec: formData.audio_codec || undefined,
      audio_bitrate: formData.audio_bitrate || undefined,
      audio_sample_rate: formData.audio_sample_rate || undefined,
      audio_channels: formData.audio_channels || undefined,
      custom_ffmpeg_args: formData.custom_ffmpeg_args || undefined,
    })
  }

  const profiles = data?.profiles || []

  const getPresetBadgeColor = (preset: string) => {
    switch (preset) {
      case 'passthrough':
        return 'bg-green-500/10 text-green-500 border-green-500/20'
      case 'universal':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20'
      case 'low_bandwidth':
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
      case 'audio_only':
        return 'bg-purple-500/10 text-purple-500 border-purple-500/20'
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20'
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">Transcoding Profiles</h1>
        <button onClick={openCreateModal} className="btn-primary shrink-0">
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Add Profile</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      <p className="text-muted-foreground">
        Transcoding profiles define how streams are processed by FFmpeg. Use presets for common scenarios
        or create custom profiles for specific requirements.
      </p>

      {/* Profiles Grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-card rounded-lg border border-border p-6 animate-pulse">
              <div className="h-12 w-12 bg-muted rounded-lg mb-4"></div>
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
            </div>
          ))
        ) : profiles.length > 0 ? (
          profiles.map((profile: TranscodeProfile) => (
            <div
              key={profile.id}
              className={`bg-card rounded-lg border border-border p-6 hover:border-muted-foreground/30 transition-colors ${
                profile.is_system ? 'opacity-90' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Settings2 className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-medium text-foreground">{profile.name}</h3>
                      {profile.is_system && (
                        <span title="System profile">
                          <Lock className="w-4 h-4 text-muted-foreground" />
                        </span>
                      )}
                    </div>
                    <span
                      className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded border ${getPresetBadgeColor(
                        profile.preset
                      )}`}
                    >
                      {profile.preset}
                    </span>
                  </div>
                </div>
                {!profile.is_system && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => openEditModal(profile)}
                      className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(profile)}
                      className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              {profile.description && (
                <p className="mt-3 text-sm text-muted-foreground">{profile.description}</p>
              )}
              <div className="mt-4 pt-4 border-t border-border">
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  {profile.video_codec && (
                    <div>
                      <span className="text-foreground">Video:</span> {profile.video_codec}
                    </div>
                  )}
                  {profile.audio_codec && (
                    <div>
                      <span className="text-foreground">Audio:</span> {profile.audio_codec}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12 text-base text-muted-foreground">
            No profiles found
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative bg-card rounded-lg border border-border p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">
                {editingProfile ? 'Edit Profile' : 'Create Profile'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-base">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-field"
                    placeholder="My Custom Profile"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Preset</label>
                  <select
                    value={formData.preset}
                    onChange={(e) => setFormData({ ...formData, preset: e.target.value as any })}
                    className="input-field"
                  >
                    <option value="passthrough">Passthrough</option>
                    <option value="universal">Universal (H.264 + AAC)</option>
                    <option value="low_bandwidth">Low Bandwidth (720p)</option>
                    <option value="audio_only">Audio Only</option>
                    <option value="custom">Custom</option>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {presetDescriptions[formData.preset]}
                  </p>
                </div>

                <div>
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input-field"
                    placeholder="Optional description"
                  />
                </div>
              </div>

              {/* Custom Settings (only shown for custom preset) */}
              {formData.preset === 'custom' && (
                <>
                  {/* Video Settings */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">
                      Video Settings
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Codec</label>
                        <select
                          value={formData.video_codec}
                          onChange={(e) => setFormData({ ...formData, video_codec: e.target.value })}
                          className="input-field"
                        >
                          <option value="">Select...</option>
                          <option value="copy">Copy (no re-encode)</option>
                          <option value="libx264">H.264 (libx264)</option>
                          <option value="libx265">H.265/HEVC (libx265)</option>
                          <option value="none">None (video disabled)</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Profile</label>
                        <select
                          value={formData.video_profile}
                          onChange={(e) => setFormData({ ...formData, video_profile: e.target.value })}
                          className="input-field"
                        >
                          <option value="">Auto</option>
                          <option value="baseline">Baseline</option>
                          <option value="main">Main</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Width</label>
                        <input
                          type="number"
                          value={formData.video_width || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, video_width: parseInt(e.target.value) || 0 })
                          }
                          className="input-field"
                          placeholder="0 = auto"
                        />
                      </div>
                      <div>
                        <label className="form-label">Height</label>
                        <input
                          type="number"
                          value={formData.video_height || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, video_height: parseInt(e.target.value) || 0 })
                          }
                          className="input-field"
                          placeholder="0 = auto"
                        />
                      </div>
                      <div>
                        <label className="form-label">Bitrate (kbps)</label>
                        <input
                          type="number"
                          value={formData.video_bitrate || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, video_bitrate: parseInt(e.target.value) || 0 })
                          }
                          className="input-field"
                          placeholder="0 = auto"
                        />
                      </div>
                      <div>
                        <label className="form-label">FPS</label>
                        <input
                          type="number"
                          value={formData.video_fps || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, video_fps: parseInt(e.target.value) || 0 })
                          }
                          className="input-field"
                          placeholder="0 = source"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Audio Settings */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">
                      Audio Settings
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Codec</label>
                        <select
                          value={formData.audio_codec}
                          onChange={(e) => setFormData({ ...formData, audio_codec: e.target.value })}
                          className="input-field"
                        >
                          <option value="">Select...</option>
                          <option value="copy">Copy (no re-encode)</option>
                          <option value="aac">AAC</option>
                          <option value="mp3">MP3</option>
                          <option value="none">None (audio disabled)</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Bitrate (kbps)</label>
                        <input
                          type="number"
                          value={formData.audio_bitrate || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, audio_bitrate: parseInt(e.target.value) || 0 })
                          }
                          className="input-field"
                          placeholder="0 = auto"
                        />
                      </div>
                      <div>
                        <label className="form-label">Sample Rate</label>
                        <select
                          value={formData.audio_sample_rate || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, audio_sample_rate: parseInt(e.target.value) || 0 })
                          }
                          className="input-field"
                        >
                          <option value="">Auto</option>
                          <option value="44100">44100 Hz</option>
                          <option value="48000">48000 Hz</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Channels</label>
                        <select
                          value={formData.audio_channels || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, audio_channels: parseInt(e.target.value) || 0 })
                          }
                          className="input-field"
                        >
                          <option value="">Auto</option>
                          <option value="1">Mono (1)</option>
                          <option value="2">Stereo (2)</option>
                          <option value="6">5.1 (6)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Advanced Settings */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-foreground border-b border-border pb-2">
                      Advanced
                    </h3>
                    <div>
                      <label className="form-label">Custom FFmpeg Arguments</label>
                      <input
                        type="text"
                        value={formData.custom_ffmpeg_args}
                        onChange={(e) => setFormData({ ...formData, custom_ffmpeg_args: e.target.value })}
                        className="input-field font-mono text-sm"
                        placeholder="-preset ultrafast -tune zerolatency"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Additional FFmpeg arguments (appended after codec settings)
                      </p>
                    </div>
                  </div>
                </>
              )}

              <div className="flex gap-3 pt-4 border-t border-border">
                <button type="button" onClick={closeModal} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePreview}
                  disabled={previewMutation.isPending}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Code className="w-4 h-4" />
                  Preview
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingProfile
                    ? 'Update'
                    : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setPreviewModalOpen(false)} />
          <div className="relative bg-card rounded-lg border border-border p-4 sm:p-6 w-full max-w-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">FFmpeg Arguments Preview</h2>
              <button
                onClick={() => setPreviewModalOpen(false)}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-secondary rounded-lg p-4 font-mono text-sm overflow-x-auto">
              <code className="text-foreground whitespace-pre-wrap break-all">
                {previewArgs.join(' ')}
              </code>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(previewArgs.join(' '))
                }}
                className="btn-secondary flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Copy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
